import { useState, useEffect, useCallback } from "react";
import {
    ContentBlock,
    ContentBlockType,
    Prompt,
    PromptShape,
    Selection,
    SelectionShape,
} from "@/models";
import {
    FormContentBlockDefaultValuesObject,
    FormValidationSchema,
} from "@/styles";
import { groupBy, isEqual } from "lodash";
import {
    getSelectionData,
    getPromptsFromContentBlocks,
    getFormValidationSchema,
    getQuestionData,
    getDescendantQuestionBlocks,
} from "@/util";
import { mapById, useAppDispatch, useAppSelector } from "@/redux-state";
import {
    gotFormData,
    gotPrompts,
    gotFormContentBlocks,
    gotFormValidationSchemaMap,
    gotPromptsByForm,
    gotSelectionsByForm,
    gotIdsForSavingSelections,
    // gotControlledFormFieldDataMap,
} from "../promptSlice";
import { useAtom } from "jotai";
import {
    formInvalidAtom,
    formSubmittingAtom,
} from "../../state/formStatusAtoms";
import { SapienInertia } from "@/inertia-utils/hooks";
import { useSetNotification } from "@/hooks/useNotificationStore";
import {
    gotDecisionLogSelections,
    gotOthersSelections,
} from "../othersSelectionsSlice";

// used in FormContentBlockProvider — returns only the prompts belonging to a specific form
export const usePromptsByForm = (formContentBlockId: string) => {
    return useAppSelector(
        (state) => state.promptStore.promptsByForm[formContentBlockId] ?? [],
    );
};

// used in SimulationDisplayWrapper
export const usePromptStore = () => {
    const dispatch = useAppDispatch();

    const setPromptAndFormBlockDataFromContentBlocks = useCallback(
        (contentBlocks: ContentBlock[]) => {
            let promptArray: (Prompt | PromptShape)[] = [];

            const allBlocksById = mapById(contentBlocks);

            const formContentBlockArray = contentBlocks.filter(
                (contentBlock) =>
                    contentBlock.content_block_type === ContentBlockType.Form,
            );

            const promptsByForm = formContentBlockArray.reduce(
                (map, formBlock) => {
                    const questionDescendants = getDescendantQuestionBlocks(
                        formBlock.id,
                        allBlocksById,
                    );

                    const promptsInForm =
                        getPromptsFromContentBlocks(questionDescendants);

                    promptArray = [...promptArray, ...promptsInForm];

                    return {
                        ...map,
                        [formBlock.id]: promptsInForm,
                    } as Record<string, Prompt[]>;
                },
                {} as Record<string, Prompt[]>,
            );

            dispatch(gotPrompts(mapById(promptArray)));
            dispatch(gotFormContentBlocks(mapById(formContentBlockArray)));
            dispatch(gotPromptsByForm(promptsByForm));
        },
        [dispatch],
    );

    const prompts = useAppSelector((state) => state.promptStore.prompts);

    return {
        setPromptAndFormBlockDataFromContentBlocks,
        prompts,
    };
};

// used in SimulationDisplayWrapper and ContentBlockDisplay
export const useOthersSelections = () => {
    const dispatch = useAppDispatch();

    const _othersSelectionsByContentBlockId = useAppSelector(
        (state) =>
            state?.othersSelectionsStore?.othersSelectionsByContentBlockId,
    );

    const decisionLogSelections = useAppSelector(
        (state) => state?.othersSelectionsStore.decisionLogSelections,
    );

    const _gotOthersSelections = useCallback(
        (selections: Selection[]) => {
            dispatch(gotOthersSelections(selections));
        },
        [dispatch],
    );

    const _gotDecisionLogSelections = useCallback(
        (selections: (SelectionShape & { prompt: PromptShape })[]) => {
            dispatch(gotDecisionLogSelections(selections));
        },
        [dispatch],
    );

    return {
        othersSelectionsByContentBlockId: _othersSelectionsByContentBlockId,
        gotOthersSelections: _gotOthersSelections,
        decisionLogSelections,
        gotDecisionLogSelections: _gotDecisionLogSelections,
    };
};

// used in SimulationDisplayWrapper
export const useGotSelections = () => {
    const dispatch = useAppDispatch();

    const _promptsByForm = useAppSelector(
        (state) => state.promptStore.promptsByForm,
    );

    const gotSelections = useCallback(
        (selections: Selection[]) => {
            let selectionsGroupedByPromptId = groupBy(selections, "prompt_id");

            let selectionsByForm = Object.keys(_promptsByForm).reduce(
                (map, formBlockId) => {
                    let selectionsInForm = _promptsByForm[formBlockId].reduce(
                        (selectionArray, prompt) => {
                            return selectionsGroupedByPromptId[prompt.id]
                                ? [
                                      ...selectionArray,
                                      ...selectionsGroupedByPromptId[prompt.id],
                                  ]
                                : selectionArray;
                        },
                        [],
                    );
                    return { ...map, [formBlockId]: selectionsInForm };
                },
                {},
            );

            dispatch(gotSelectionsByForm(selectionsByForm));
        },
        [dispatch, _promptsByForm],
    );

    return {
        gotSelections,
    };
};

// used in SimulationDisplayWrapper
export const useGotIdsForSavingSelections = () => {
    const dispatch = useAppDispatch();

    const _gotIdsForSavingSelections = useCallback(
        (teamId: string, simulationId: string) => {
            dispatch(
                gotIdsForSavingSelections({
                    teamId,
                    simulationId,
                }),
            );
        },
        [dispatch],
    );

    return { gotIdsForSavingSelections: _gotIdsForSavingSelections };
};

// Form validity / submitting now live in jotai atoms (see state/formStatusAtoms),
// not the redux promptStore. The hook API is unchanged so every consumer
// (ButtonTheme, FormContentBlockProvider, NestedContentBlockDisplay, ...) keeps
// calling useIsSubmitting() exactly as before. The large form state — prompts,
// selections, formData — stays in redux; only these two booleans moved.
export const useIsSubmitting = () => {
    const [_isSubmitting, setSubmitting] = useAtom(formSubmittingAtom);
    const [_isInvalid, setInvalid] = useAtom(formInvalidAtom);

    const _setSubmitting = useCallback(
        () => setSubmitting(true),
        [setSubmitting],
    );
    const _setDoneSubmitting = useCallback(
        () => setSubmitting(false),
        [setSubmitting],
    );
    const _setValid = useCallback(() => setInvalid(false), [setInvalid]);
    const _setInValid = useCallback(() => setInvalid(true), [setInvalid]);

    return {
        isSubmitting: _isSubmitting,
        setIsSubmitting: _setSubmitting,
        setDoneSubmitting: _setDoneSubmitting,
        isValid: !_isInvalid,
        setValid: _setValid,
        setInvalid: _setInValid,
    };
};
// used in NestedContentBlockDisplay
export const useParticipantBlockFormProps = (formContentBlockId: string) => {
    const { setIsSubmitting } = useIsSubmitting();
    const setNotification = useSetNotification();

    const questionFormObject = useAppSelector(
        (state) => state.promptStore.formData[formContentBlockId],
    );
    const teamId = useAppSelector((state) => state.promptStore.teamId);
    const simulationId = useAppSelector(
        (state) => state.promptStore.simulationId,
    );
    const prompts = useAppSelector(
        (state) => state.promptStore.promptsByForm[formContentBlockId],
    );
    const selections = useAppSelector(
        (state) => state.promptStore.selectionsByForm[formContentBlockId],
    );
    const formValidationSchema = useAppSelector(
        (state) =>
            state.promptStore.formValidationSchemaMap[formContentBlockId],
    );

    const getSaveSelectionsFunction = (
        teamId: string,
        simulationId: string,
    ) => {
        const submit = async (selections: Selection[]) => {
            setIsSubmitting();
            const postBody: any = {
                selections,
                team_id: teamId,
                simulation_id: simulationId,
            };
            SapienInertia.post(
                "selection.store",
                postBody,
                {},
                {
                    onError(page) {
                        console.log("onError", page, typeof page);
                    },
                    onSuccess() {
                        // Fire the submitter's confirmation toast client-side.
                        // It used to be a server session-flash, but the teammate
                        // poll races the submit's reload for that one-shot flash
                        // (whichever request lands first ages it out), so the
                        // submitter often missed their own toast. Local + instant
                        // sidesteps the race entirely.
                        setNotification({
                            autoClose: 3000,
                            text: "Success! Your submission has been recorded.",
                            isInline: true,
                            textColor: "#4B5563",
                            backgroundColor: "#F0FFF4",
                            boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.08)",
                        });
                    },
                    preserveScroll: true,
                    only: [
                        "contentBlocks",
                        "othersSelections",
                        "selections",
                        "decisionLogSelections",
                    ],
                },
            );
        };

        return submit;
    };

    const getOnFormSubmitFunction = (
        prompts: Prompt[],
        selections: Selection[],
        saveSelections: (selections: Selection[]) => Promise<void>,
    ) => {
        // FormContainer onSubmit
        // gets passed raw formObject from react-hook-form,
        // controlled question object added in,
        // selections recreated with helper function
        const onFormSubmit = async (
            formObject: FormContentBlockDefaultValuesObject,
        ) => {
            let selectionsToSave = getSelectionData(
                prompts,
                selections,
                formObject,
                "",
            );

            if (saveSelections) {
                await saveSelections(selectionsToSave);
            } else {
                console.log(selectionsToSave);
            }
        };

        return onFormSubmit;
    };

    const saveSelections = useCallback(
        getSaveSelectionsFunction(teamId, simulationId),
        [teamId, simulationId],
    );

    const onFormSubmit = useCallback(
        getOnFormSubmitFunction(prompts, selections, saveSelections),
        [prompts, selections, saveSelections],
    );

    const [localQuestionFormObject, setLocalQuestionFormObject] =
        useState<FormContentBlockDefaultValuesObject>();

    useEffect(() => {
        if (
            !!questionFormObject &&
            !isEqual(questionFormObject, localQuestionFormObject)
        ) {
            setLocalQuestionFormObject(questionFormObject);
        }
    }, [questionFormObject]);

    return {
        questionFormObject: localQuestionFormObject,
        onFormSubmit,
        validationSchema: formValidationSchema,
    };
};

// used in SimulationDisplayWrapper
export const useParticipantFormAndQuestionData = () => {
    const dispatch = useAppDispatch();

    const _formContentBlocks = useAppSelector(
        (state) => state.promptStore.formContentBlocks,
    );
    const _promptsByForm = useAppSelector(
        (state) => state.promptStore.promptsByForm,
    );
    const _selectionsByForm = useAppSelector(
        (state) => state.promptStore.selectionsByForm,
    );

    useEffect(() => {
        if (
            !!_formContentBlocks &&
            !!_promptsByForm &&
            !!_selectionsByForm &&
            Object.keys(_formContentBlocks).length &&
            Object.keys(_promptsByForm).length &&
            Object.keys(_selectionsByForm).length
        ) {
            let newFormData: {
                [index: string]: FormContentBlockDefaultValuesObject;
            } = {};

            let newFormValidationSchemaMap: {
                [formContentBlockId: string]: FormValidationSchema;
            } = {};

            Object.keys(_formContentBlocks).forEach((formBlockId) => {
                let newQuestionFormObject =
                    {} as FormContentBlockDefaultValuesObject;

                if (
                    _promptsByForm[formBlockId] !== undefined &&
                    _promptsByForm[formBlockId].length > 0 &&
                    _selectionsByForm[formBlockId] !== undefined
                ) {
                    let groupedSelections = groupBy(
                        _selectionsByForm[formBlockId] || [],
                        "prompt_id",
                    );

                    _promptsByForm[formBlockId].forEach((prompt) => {
                        let data = getQuestionData(
                            prompt,
                            prompt.options || [],
                            groupedSelections[prompt.id] || [],
                        );

                        newQuestionFormObject = {
                            ...newQuestionFormObject,
                            [prompt.id]: data,
                        } as FormContentBlockDefaultValuesObject;
                    });
                }

                newFormData = {
                    ...newFormData,
                    [formBlockId]: newQuestionFormObject,
                };

                newFormValidationSchemaMap = {
                    ...newFormValidationSchemaMap,
                    [formBlockId]: getFormValidationSchema(
                        _promptsByForm[formBlockId] || [],
                    ),
                };
            });

            dispatch(gotFormData(newFormData));
            dispatch(gotFormValidationSchemaMap(newFormValidationSchemaMap));
        }
    }, [_formContentBlocks, _promptsByForm, _selectionsByForm, dispatch]);

    return {};
};

// keep some logic for controlled data in case we want to set it when watched form values change
// export const useParticipantControlledFormFieldDataMap = () => {
//     const dispatch = useAppDispatch();

//     const _controlledFormFieldDataMap = useAppSelector(
//         (state) => state.promptStore.controlledFormFieldDataMap
//     );

//     const _gotControlledFormFieldDataMap = useCallback(
//         (map: {
//             [formContentBlockId: string]: {
//                 [promptId: string]: SelectionShape[];
//             };
//         }) => {
//             dispatch(gotControlledFormFieldDataMap(map));
//         },
//         [dispatch]
//     );

//     // const setControlledFormFieldDataMap = useCallback(
//     //     (
//     //         formBlockId: string,
//     //         promptId: string,
//     //         selections: SelectionShape[]
//     //     ) => {
//     //         let newControlledFormFieldDataMap = {
//     //             ..._controlledFormFieldDataMap,
//     //             [formBlockId]: {
//     //                 ..._controlledFormFieldDataMap[formBlockId],
//     //                 [promptId]: selections,
//     //             },
//     //         };

//     //         _gotControlledFormFieldDataMap(newControlledFormFieldDataMap);
//     //     },
//     //     [_controlledFormFieldDataMap, _gotControlledFormFieldDataMap]
//     // );

//     return {
//         controlledFormFieldDataMap: _controlledFormFieldDataMap,
//         gotControlledFormFieldDataMap: _gotControlledFormFieldDataMap,
//         // setControlledFormFieldDataMap,
//     };
// };
